import { inject, Injectable, OnDestroy } from '@angular/core';
import { interval, map, Observable, ReplaySubject, shareReplay, takeUntil, timer } from 'rxjs';
import { ActivityEventStack } from './activity-event-stack';
import { AnalyticsService } from '../analytics/analytics.service';
import { ZodError } from 'zod';
import { ActivityUtilities } from './models/activity-utilities';
import { HttpService } from '../http/http.service';
import { OperatorService } from '../operator/operator.service';
import { JobService } from '../job/job.service';
import {
  ActivityFeedData,
  ActivityMessageData,
  ActivityMessageDataSchema,
} from '../../api/wager/schemas/activity-feed';
import { WagerApi } from '../../api/wager/wager-api';

class LimitedList<T> {
  items: T[] = [];

  constructor(private readonly maxItems: number) {}

  addItem(data: T) {
    this.items.push(data);

    if (this.items.length > this.maxItems) {
      this.items.shift();
    }
  }

  addItems(data: T[]) {
    this.items.push(...data);

    if (this.items.length >= this.maxItems) {
      this.items.splice(0, this.items.length - this.maxItems);
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class ActivityFeedService implements OnDestroy {
  private httpService = inject(HttpService);
  private analyticsService = inject(AnalyticsService);
  private operatorService = inject(OperatorService);
  private jobService = inject(JobService);

  private readonly displayBufferInterval = 120 * 1000;
  private readonly maxMiniDisplayMessages = 4;
  private readonly maxFeedMessages = 100;

  private hasInitialised = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private eventStack = new ActivityEventStack();

  oneSecSyncInterval$: Observable<Date>;
  miniDisplayMessages = new LimitedList<ActivityMessageData>(this.maxMiniDisplayMessages);
  feedMessages = new LimitedList<ActivityMessageData>(this.maxFeedMessages);

  constructor() {
    this.oneSecSyncInterval$ = interval(1000).pipe(
      map(() => new Date()),
      shareReplay(1), // Share the same observable across all subscribers
    );
  }

  initialize() {
    if (this.hasInitialised) {
      return;
    }
    this.hasInitialised = true;

    timer(0, this.displayBufferInterval)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateActivityFeed();
      });

    this.oneSecSyncInterval$.pipe(takeUntil(this.destroyed$)).subscribe((now) => {
      this.processEvents(now);
    });
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private processEvents(now: Date): void {
    if (this.eventStack.hasEvents()) {
      const dueEvents = this.eventStack
        .popDueEvents(now)
        .filter(
          (dueEvent) =>
            !this.feedMessages.items.some((item) => item.activityId === dueEvent.activityId),
        );
      this.feedMessages.addItems(dueEvents);
      this.miniDisplayMessages.addItems(dueEvents);
    }
  }

  private async updateActivityFeed() {
    const data = await this.fetchActivityFeed();
    if (data) {
      const activityMessages = this.parseActivityFeed(data);

      // Stretch the dates so that they appear within 'now' and 'now + bufferInterval'
      const transformedMessages = ActivityUtilities.transformDisplayDates(
        activityMessages,
        Date.now(),
        this.displayBufferInterval,
      );

      // Push new activities onto the event stack
      this.eventStack.pushEvents(transformedMessages);
    }
  }

  private async fetchActivityFeed(): Promise<ActivityFeedData | undefined> {
    const gameId = this.operatorService.gameId;
    const job = this.jobService.create(async () =>
      WagerApi.request(this.httpService, this.analyticsService).activityFeed(gameId),
    );
    return await job.runNoThrow();
  }

  private parseActivityFeed(activityFeed: ActivityFeedData): ActivityMessageData[] {
    const activityMessages: ActivityMessageData[] = [];

    const activities = activityFeed.activities;
    for (const time in activities) {
      const activitiesAtTime = activities[time];

      for (const activityId in activitiesAtTime) {
        try {
          const cachedActivity = activitiesAtTime[activityId];
          ActivityMessageDataSchema.parse(cachedActivity);
          activityMessages.push(cachedActivity);
        } catch (error) {
          if (error instanceof ZodError) {
            console.error('Zod Validation Error: ', error.errors);
          }
          this.analyticsService.handleError(error, 'Parse activity feed');
        }
      }
    }
    return activityMessages;
  }
}
